import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { Section } from "../../../../components/primitives/layout/Section";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { SearchBar } from "../../../../components/primitives/inputs/SearchBar";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";
import {
  buildCatalogueDocumentsPath,
  buildCatalogueSubjectsPath,
  filterCatalogueFields,
  listCatalogueFields,
  type CatalogueField,
} from "../../../../lib/corpusCatalogueView";
import { LibraryPage } from "../LibraryPage";
import { useCatalogueFields } from "./useCatalogueFields";

/** The corpus' ingested papers by health field. A row leads to the main subjects of the field, and
 *  its button to the papers it is made of. */
export default function CatalogueFields() {
  const navigate = useNavigate();
  const taxonomy = useTaxonomy();
  const fields = useCatalogueFields();
  const [query, setQuery] = useState("");
  const fieldRows = useMemo(
    () =>
      filterCatalogueFields(
        fields.data ? listCatalogueFields(fields.data.byField, taxonomy) : [],
        query
      ),
    [fields.data, taxonomy, query]
  );

  const columns: DataSequenceColumn<CatalogueField>[] = [
    { key: "name", header: "Field", cardRole: "primary", cell: (row) => row.name },
    { key: "papers", header: "Papers", align: "right", cell: (row) => row.papersText },
    {
      key: "action",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <Button
          component={Link}
          to={buildCatalogueDocumentsPath({ field: row.urn })}
          size="small"
          onClick={(event) => event.stopPropagation()}
        >
          Papers
        </Button>
      ),
    },
  ];

  return (
    <LibraryPage>
      <Section>
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search a health field by name…"
          ariaLabel="Search health fields"
        />
        {fields.loading ? (
          <LoadingState />
        ) : fields.error ? (
          <Alert severity="error">{fields.error}</Alert>
        ) : (
          <DataSequence
            columns={columns}
            rows={fieldRows}
            getRowId={(row) => row.urn}
            onRowClick={(row) => navigate(buildCatalogueSubjectsPath(row.urn))}
            emptyMessage={
              query.trim()
                ? "No health field matches."
                : "No ingested paper has a health field yet."
            }
          />
        )}
      </Section>
    </LibraryPage>
  );
}
